//May13 FormSubmissionsContext with bitly integration

import { db, functions } from '../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

import React, { createContext, useContext, useState, useEffect } from 'react';
import { useFormContext } from './FormContext';
import { useFormActions } from './FormActionsContext';
import { useFormSubmissionSettings } from './FormSubmissionSettingsContext';
import { replaceChipsWithValues } from "../utils/formUtils"; 
import {
  getFormById,
  //submitFormData,
  getFormSubmissions,
  getFormSubmissionsCount
} from '../utils/firestore';
import { safeGetItem, safeSetItem, safeRemoveItem } from '../utils/storageUtils';
import { useAuth } from '../contexts/AuthContext';  // used to fetch userId
import { getViewerId, getSessionViewerId } from '../utils/visitorId';
import {
  ONBOARDING_EVENTS,
  ONBOARDING_EVENT_SOURCES,
  SUBMISSION_MODES,
} from '../utils/onboardingLifecycle';

// De-duplicate by canonical id; tolerate rows that lack both (shouldn't happen for server rows)
function dedupeById(rows) {
  const map = new Map();
  for (const r of rows) {
    const k = r?.submissionId ?? r?.id;
    if (k) map.set(k, r);
    else map.set(JSON.stringify(r), r);
  }
  return Array.from(map.values());
}

/**
 * Returns true only for plain object literals.
 *
 * Enterprise integration note:
 * - Firestore Timestamp objects, Date objects, arrays, and provider SDK objects
 *   should be treated as values, not recursively merged.
 * - This keeps provider metric patches safe when they contain timestamps or
 *   nested provider-owned payloads.
 */
function isPlainPatchObject(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }

  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

/**
 * Deep-merges plain object literals while preserving non-plain values.
 *
 * Enterprise integration note:
 * - Used only for local UI reconciliation after backend-owned provider metric
 *   sync returns cached Firestore fields.
 * - This does not mutate the original row.
 * - This does not call Bitly.
 * - This does not alter submission persistence logic.
 */
function mergePlainPatchObjects(base = {}, patch = {}) {
  if (!isPlainPatchObject(patch)) {
    return patch;
  }

  const safeBase = isPlainPatchObject(base) ? base : {};
  const next = { ...safeBase };

  Object.entries(patch).forEach(([key, patchValue]) => {
    const baseValue = safeBase[key];

    if (
      isPlainPatchObject(baseValue) &&
      isPlainPatchObject(patchValue)
    ) {
      next[key] = mergePlainPatchObjects(baseValue, patchValue);
      return;
    }

    next[key] = patchValue;
  });

  return next;
}

/**
 * Resolves the Firestore document id used for backend provider metric patches.
 *
 * Important:
 * - Prefer row.id because fetched/persisted submissions use Firestore doc ids.
 * - submissionId can be a client-generated logical id or bulk parent/row id,
 *   so we intentionally do not use it as the primary patch key.
 * - local_* rows are optimistic UI rows and should not be patched by backend
 *   provider metric sync until they have a real Firestore document id.
 */
function getPatchableSubmissionRowId(row = {}) {
  const id = row?.id ? String(row.id) : '';

  if (id && !id.startsWith('local_')) {
    return id;
  }

  const submissionDocId = row?.submissionDocId ?
    String(row.submissionDocId) :
    '';

  if (submissionDocId && !submissionDocId.startsWith('local_')) {
    return submissionDocId;
  }

  return null;
}

/**
 * Merges a backend-returned provider metric patch into a loaded submission row.
 *
 * Enterprise integration note:
 * - Provider-owned namespaces are deep-merged so a Bitly click patch cannot
 *   erase an existing shortUrl/linkId/status written by the shortlink processor.
 * - Top-level cached metric fields such as bitlyClicksTotal are still applied.
 * - Existing submission responses, action outputs, asset metadata, optimistic
 *   shortlink hints, and pagination fields remain untouched unless explicitly
 *   present in the backend patch.
 */
function mergeSubmissionProviderMetricPatch(row = {}, patch = {}) {
  if (!isPlainPatchObject(patch)) {
    return row;
  }

  const next = {
    ...row,
    ...patch,
  };

  if (Object.prototype.hasOwnProperty.call(patch, 'shortlinks')) {
    next.shortlinks = mergePlainPatchObjects(
      row.shortlinks || {},
      patch.shortlinks || {}
    );
  }

  if (Object.prototype.hasOwnProperty.call(patch, 'providerOutputs')) {
    next.providerOutputs = mergePlainPatchObjects(
      row.providerOutputs || {},
      patch.providerOutputs || {}
    );
  }

  if (Object.prototype.hasOwnProperty.call(patch, 'integrations')) {
    next.integrations = mergePlainPatchObjects(
      row.integrations || {},
      patch.integrations || {}
    );
  }

  return next;
}

/* ──────────────────────────────────────────────────────────────
   submitFormWithAssets (HTTP) – calls the new Cloud Function
   Expects caller to provide a fresh Firebase ID token.
   ──────────────────────────────────────────────────────────── */
const SUBMIT_WITH_ASSETS_URL =
  "https://us-central1-myutms-f8cba.cloudfunctions.net/submitFormWithAssets";

async function submitFormWithAssetsHttp(idToken, payload) {
  const res = await fetch(SUBMIT_WITH_ASSETS_URL, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${idToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(
      `[submitFormWithAssetsHttp] HTTP ${res.status} ${text || ""}`.trim()
    );
  }
  return res.json();
}

const FormSubmissionsContext = createContext();
export const useFormSubmissions = () => useContext(FormSubmissionsContext);

export const FormSubmissionsProvider = ({ children }) => {
  const { formId } = useFormContext();
  const { currentUser, organizationId } = useAuth();
 const viewerId = getViewerId();      
 const viewerSessionId = getSessionViewerId();

  const { handleSubmit: processFormActions } = useFormActions();
  const { submissionSettings, patchActionRuntime, getSubmissionUIComponent } = useFormSubmissionSettings();


  const [submissions, setSubmissions] = useState([]);

  // Column order is now owned by FormSubmissionSettingsContext.
  //
  // Enterprise cleanup:
  // - Do not fetch legacy per-user column order on every published form view.
  // - In-form Display Table uses addToSubmissionsTable.columnOrder.
  // - Full Submissions page uses submissionsPageTable.columnOrder with fallback
  //   to addToSubmissionsTable.columnOrder.
  // - Keep `columnOrder` in this context value as a backwards-compatible,
  //   derived value for any older consumers that still destructure it.
  const columnOrder = React.useMemo(() => {
    const sharedOrder =
      submissionSettings?.submissionsPageTable?.columnOrder ??
      submissionSettings?.addToSubmissionsTable?.columnOrder ??
      [];

    return Array.isArray(sharedOrder) ? sharedOrder : [];
  }, [
    submissionSettings?.submissionsPageTable?.columnOrder,
    submissionSettings?.addToSubmissionsTable?.columnOrder,
  ]);

  const [submitStatus, setSubmitStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [submissionMessage, setSubmissionMessage] = useState('');
  const [isTestingMode, setIsTestingMode] = useState(false); // 🔹 Tracks if form exists only in session storage

  //  Pagination state
  const [lastVisible, setLastVisible] = useState(null);
  const [hasMoreSubmissions, setHasMoreSubmissions] = useState(true);
  const [rowCount, setRowCount] = useState(-1); 
  const [pageSize, setPageSize] = useState(25);
 const [onlyMineScope, setOnlyMineScope] = useState(true); // true=session-only, false=all
 const [refetchRequested, setRefetchRequested] = useState(false); // gate a deferred refetch

// Prevent overlapping fetches that double-append the same page
 const isFetchingRef = React.useRef(false);

 /**
  * Patches Display Card runtime state with persisted submission metadata.
  *
  * Enterprise integration note:
  * - This does not listen to Firestore.
  * - This does not call Bitly.
  * - This does not block submission persistence.
  * - DisplayCardPreview owns UI-only hydration for backend-enriched results.
  */
 const patchDisplayCardSubmissionRuntime = React.useCallback((patch = {}) => {
   if (
     !submissionSettings?.displayInCard?.enabled ||
     typeof patchActionRuntime !== 'function'
   ) {
     return;
   }

   patchActionRuntime('displayInCard', patch);

   console.log('[FormSubmissionsContext] Patched Display Card runtime:', {
     patch,
   });
 }, [
   patchActionRuntime,
   submissionSettings?.displayInCard?.enabled,
 ]);

 /**
  * Patches already-loaded submission rows by Firestore document id.
  *
  * Enterprise integration note:
  * - Provider metric sync remains backend-owned.
  * - This helper only updates local UI state with backend-returned cached
  *   provider metric fields.
  * - It avoids a full table refetch after a small visible-page Bitly click
  *   sync.
  * - It is provider-neutral and can support future provider metric syncs.
  * - It does not call Bitly, does not write Firestore, and does not modify the
  *   submit/persist path.
  */
 const patchSubmissionsById = React.useCallback((patchesById = {}) => {
   if (
     !patchesById ||
     typeof patchesById !== 'object' ||
     Array.isArray(patchesById)
   ) {
     console.warn(
       '[FormSubmissionsContext] Ignoring invalid submission patch map:',
       {
         receivedType: Array.isArray(patchesById) ?
           'array' :
           typeof patchesById,
       }
     );
     return;
   }

   const patchIds = Object.keys(patchesById).filter(Boolean);

   if (patchIds.length === 0) {
     return;
   }

   setSubmissions((prev) => {
     let appliedCount = 0;
     const unmatchedPatchIds = new Set(patchIds);

     const nextRows = (prev || []).map((row) => {
       const rowId = getPatchableSubmissionRowId(row);

       if (!rowId) {
         return row;
       }

       const patch = patchesById[rowId];

       if (!patch || !isPlainPatchObject(patch)) {
         return row;
       }

       unmatchedPatchIds.delete(rowId);
       appliedCount += 1;

       return mergeSubmissionProviderMetricPatch(row, patch);
     });

     console.log('[FormSubmissionsContext] Patched submissions by id:', {
       requestedCount: patchIds.length,
       appliedCount,
       unmatchedCount: unmatchedPatchIds.size,
       unmatchedIds: Array.from(unmatchedPatchIds).slice(0, 10),
       patchedIds: patchIds.slice(0, 10),
     });

     return nextRows;
   });
 }, []);

  /**
   * Records the v1 activation milestone after a submission is successfully
   * saved/persisted.
   *
   *
   * Enterprise rules:
   * - Do not block form submission UX if lifecycle recording fails.
   * - Do not activate anonymous public viewers.
   * - Use one event per submit click, even for bulk submissions.
   * - Use deterministic eventId for idempotency.
   */
  const recordSubmissionOnboardingEvent = async ({
    submissionId,
    eventSubmissionId = null,
    submissionMode = SUBMISSION_MODES.UNKNOWN,
    isBulk = false,
    rowCount = 1,
    persistedCount = 1,
    failedCount = 0,
    metadata = {},
  } = {}) => {
    if (!currentUser?.uid) {
      console.log(
        '[FormSubmissionsContext] Skipping onboarding activation event ' +
        'because no authenticated user is present.'
      );
      return null;
    }

    if (!submissionId) {
      console.warn(
        '[FormSubmissionsContext] Skipping onboarding activation event ' +
        'because submissionId is missing.'
      );
      return null;
    }

    try {
      const recordOnboardingEvent = httpsCallable(
        functions,
        'recordOnboardingEvent'
      );

      const eventId =
        `submission_${currentUser.uid}_${submissionId}`;

      const response = await recordOnboardingEvent({
        eventName: ONBOARDING_EVENTS.CAMPAIGN_URL_SUBMISSION_CREATED,
        eventId,
        insertId: eventId,
        eventSource: ONBOARDING_EVENT_SOURCES.FORM_SUBMISSIONS_CONTEXT,
        formId,
        submissionId: eventSubmissionId || submissionId,
        eventProperties: {
          submissionMode,
          formId,
          submissionId: eventSubmissionId || submissionId,
        },
        metadata: {
          ...metadata,
          source: 'FormSubmissionsContext',
          submissionMode,
          isBulk,
          rowCount,
          persistedCount,
          failedCount,
        },
      });

      console.log('[FormSubmissionsContext] Onboarding activation recorded:', {
        eventId,
        submissionId,
        eventSubmissionId: eventSubmissionId || submissionId,
        result: response?.data || null,
      });

      return response?.data || null;
    } catch (eventErr) {
      console.warn(
        '[FormSubmissionsContext] Onboarding activation failed (non-fatal):',
        {
          submissionId,
          eventSubmissionId,
          error: eventErr?.message || eventErr,
        }
      );

      return null;
    }
  };


/* -------------------------------------------------------------
   Default range  =  00:00 six days ago  →  this very moment
   (today counts as day 7, matching common “last 7 days” UX)    */
const computeLast7Days = () => {
  const today     = new Date();             // now
  const sixDays   = new Date(today);        // clone
  sixDays.setDate(today.getDate() - 6);     // 6 days back
  sixDays.setHours(0, 0, 0, 0);             // 00:00:00.000
  return [sixDays, today];
};

const [dateRange, setDateRange] = useState(computeLast7Days);

const userSubmissions = React.useMemo(() => {
  const strict = (submissions || []).filter(
    (row) => row?.submitterSessionId === viewerSessionId
  );

  if (strict.length > 0) return strict;

  // DEV-only rescue for HMR/fast-refresh id hops
  if (process.env.NODE_ENV !== 'production') {
    const byViewer = (submissions || []).filter(
      (row) => row?.submitterId === viewerId
    );
    return byViewer;
  }

  return strict; // production stays strict
}, [submissions, viewerSessionId, viewerId]);

  /**
   * Determines submission source:
   * - **Testing in Builder (New Form)** → Uses session storage.
   * - **Saved Drafts** → Uses session storage but checks Firebase metadata.
   * - **Published Forms** → **ONLY fetches from Firestore**.
   */
 const fetchSubmissions = async (
   loadMore     = false,
   sizeOverride = null,
   onlyMine     = onlyMineScope
 ) => {


    if (!formId) return;

    // avoid overlapping calls that would double-append
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;

    setLoading(true);

    setSubmitStatus('');
    setError(null);

    try {
      const formDetails = await getFormById(formId);

      // 🔹 Case 1: **Form isn't in Firebase (Testing in Builder)**
      if (!formDetails || formDetails.status === 'error' || !formDetails.form) {
        console.warn(`[FormSubmissionsContext] Form ${formId} not found in Firebase. Checking session storage.`);
        setIsTestingMode(true);

        const storedDraftSubmissions = safeGetItem(`draft_${formId}_submission`);
        setSubmissions(storedDraftSubmissions || []);
      setRowCount((storedDraftSubmissions || []).length);
        return;
      }

      // 🔹 Case 2: **Form is saved as a draft in Firebase**
      const formStatus = formDetails.form.status;
      if (formStatus === 'draft') {
        console.warn(`[FormSubmissionsContext] Form ${formId} is a Firebase draft. Checking session storage.`);
        setIsTestingMode(false);

        const storedDraftSubmissions = safeGetItem(`draft_${formId}_submission`);
        setSubmissions(storedDraftSubmissions || []);
      setRowCount((storedDraftSubmissions || []).length);
        return;
      }

      // 🔹 Case 3: **Form is published - Fetch only from Firestore**
//      console.log(`[FormSubmissionsContext] Form ${formId} is published. Fetching only from Firestore.`);
      setIsTestingMode(false);
/* (1) on the very first page request, get a server aggregate) */
 if (!loadMore) {
  try {
    const total = await getFormSubmissionsCount(
      formId,
      dateRange?.[0] ?? null,
      dateRange?.[1] ?? null,
      null,                                   // keep submitterId null
      onlyMine ? viewerSessionId : null       // ★ session count ONLY when onlyMine=true
    );
    setRowCount(total);
  } catch (cntErr) {
    console.error('[FSC] count() failed:', cntErr);
   // keep previous rowCount; do not force -1 here
  }
}



      /* (2) fetch the actual page) */
      const response = await getFormSubmissions(
        formId,
        loadMore ? lastVisible : null,
        // pageSize,
        sizeOverride ?? pageSize,
        dateRange?.[0] ?? null,          // lower-bound
        dateRange?.[1] ?? null,          // upper-bound
        'submittedAt',                   // 🔸 sort field
        'desc',                          // 🔸 newest → oldest
        null,                            // keep submitterId null
        onlyMine ? viewerSessionId : null // ★ NEW: session filter preferred
      );

      if (response.status === 'success') {
        setSubmissions(prev => {
          if (!loadMore) return response.submissions; // first page / replacements
          // append with de-dupe (handles accidental double-append or cursor overlap)
          return dedupeById([...prev, ...response.submissions]);
        });
        setLastVisible(response.lastVisible);
        setHasMoreSubmissions(response.lastVisible !== null);
        setSubmitStatus('Submissions fetched successfully');
      } else {
        throw new Error(response.message);
      }

    } catch (err) {
      setError(err.message);
      console.error("[FormSubmissionsContext] Error fetching submissions:", err);
    } finally {
      isFetchingRef.current = false;   // ✅ release latch
      setLoading(false);
    }
};

// Allow pages to set the viewing scope (session-only vs all)
 const setScope = React.useCallback((onlyMine) => {
   const next = !!onlyMine;
   setOnlyMineScope(prev => {
     if (prev === next) return prev;
     // reset pagination window & counters; list will be refetched by effects below
     setLastVisible(null);
     setRowCount(-1);
     setSubmissions([]);
     setRefetchRequested(true); // ask for a fetch (deferred if one is in-flight)
     return next;
   });
 }, []);



/* ------------------------------------------------------------------ */
/* re)load whenever formId OR dateRange changes       */
/* ------------------------------------------------------------------ */
useEffect(() => {
  // wait until both formId and the default dateRange exist
  if (!formId || !dateRange) return;

  // Reset pagination for the new slice (new form/date/scope)
  setLastVisible(null);
  setSubmissions([]);

  // If nothing is running, kick off a fetch now; if something is running,
  // mark a deferred fetch and let the next effect handle it cleanly.
  if (isFetchingRef.current) {
    setRefetchRequested(true);
    return;
  }
  fetchSubmissions(false, null, onlyMineScope);

/* eslint-disable-next-line react-hooks/exhaustive-deps */
}, [formId, dateRange, onlyMineScope]);


useEffect(() => {
  // If we queued a refetch during an in-flight request,
  // run it as soon as loading flips false (using latest scope/date/form).
  if (!refetchRequested) return;
  if (loading) return;

  setRefetchRequested(false);
  fetchSubmissions(false, null, onlyMineScope);
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [refetchRequested, loading, onlyMineScope, formId, dateRange]);

  /**
   * Handles form submission:
   * - Calls `FormActionsContext` for validation & transformations.
   * - Saves to **session storage** (new form or draft) OR **Firestore** (published).
   */
const handleSubmit = async (submissionData, userData) => {
  const __t0 = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
  console.log('[SUBMIT:TIMER] handleSubmit start', { formId });

  setLoading(true);
  setSubmitStatus('');
  setError(null);

  try {
    // 1️⃣ Determine form mode (new/draft/published)
    const formDetails   = await getFormById(formId);
    const isTestingMode = !formDetails || formDetails.status === 'error' || !formDetails.form;
    const isDraftMode   = !isTestingMode && formDetails.form.status === 'draft';

    // Sharing-aware submit policy (aligns with RequireAccess + ShareForm schema)
    const isPublishedMode = !isTestingMode && !isDraftMode;
    const isPublicForm = !!formDetails?.form?.isPublic;   // ← your schema: forms/{id}.isPublic
    const canSubmitAnonymously = isPublicForm;            // policy: public link allows anonymous submit

    // ────────────────────────────────────────────────────────────────
    // Enterprise schema consistency:
    //  • Firestore docs: organizationId is string | null
    //  • CF payload:     organizationId is string ("" allowed)
    //
    // Enterprise integration note:
    // - AuthContext is preferred for authenticated builder/app sessions.
    // - Published public forms may submit anonymously, so AuthContext may not
    //   have organizationId even though the form document does.
    // - Bitly shortlink queueing requires organizationId on formSubmissions.
    //   Falling back to the form document keeps public form shortlinks reliable
    //   without moving Bitly logic into the client.
    // ────────────────────────────────────────────────────────────────
    const resolvedOrganizationId =
      organizationId ||
      formDetails?.form?.organizationId ||
      formDetails?.form?.orgId ||
      null;

    const orgIdForDocs = resolvedOrganizationId || null;
    const orgIdForPayload = resolvedOrganizationId || "";

    // ────────────────────────────────────────────────────────────────
    // Display Table Bitly runtime hint (UI-only)
    // ----------------------------------------------------------------
    // Enterprise integration rule:
    // - FormSubmissionsContext must remain lightweight.
    // - It must NOT call Bitly.
    // - It must NOT listen to Firestore.
    // - It must NOT fetch Bitly clicks or metrics.
    //
    // This flag only marks newly-created optimistic Display Table rows so
    // provider-output cells can show "Getting URL..." immediately while the
    // backend-owned shortlink processor writes shortlinks.bitly.shortUrl.
    //
    // We also require a mapped destinationUfi so a half-configured Bitly toggle
    // cannot create a misleading pending state.
    // ────────────────────────────────────────────────────────────────
    const displayTableBitlyDestinationUfi =
      submissionSettings?.addToSubmissionsTable?.shortlinkDestination?.destinationUfi ||
      submissionSettings?.addToSubmissionsTable?.bitlyDestinationUfi ||
      null;

    const isDisplayTableBitlyEnabled =
      isPublishedMode &&
      submissionSettings?.addToSubmissionsTable?.enabled === true &&
      submissionSettings?.addToSubmissionsTable?.enableBitly === true &&
      Boolean(displayTableBitlyDestinationUfi);

    const buildDisplayTableBitlyPendingPatch = () => {
      if (!isDisplayTableBitlyEnabled) {
        return {};
      }

      return {
        _pendingShortlinks: {
          bitly: {
            provider: 'bitly',
            status: 'queued',
            source: 'displayTable',
            destinationUfi: displayTableBitlyDestinationUfi,
          },
        },
      };
    };

    if (isDisplayTableBitlyEnabled) {
      console.log('[FormSubmissionsContext][displayTable][bitly] optimistic pending state enabled:', {
        formId,
        destinationUfi: displayTableBitlyDestinationUfi,
      });
    }

    // 2️⃣ Process the submission via FormActionsContext
    console.log("[FormSubmissionsContext] Passing submission data to FormActionsContext...");
    const { submissionId, submissionData: processedSubmission, bulkRows } =
      await processFormActions(submissionData, userData);

    console.log(
      '[SUBMIT:TIMER] after FormActionsContext',
      Math.round(((typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now()) - __t0),
      'ms',
      { submissionId }
    );

    if (!processedSubmission || !Array.isArray(processedSubmission)) {
      throw new Error("[FormSubmissionsContext] Error processing submission actions.");
    }

    console.log("[FormSubmissionsContext] Submission Data after processing:", processedSubmission);

    /* ────────────────────────────────────────────────────────────────
     * BULK (URL CSV) — fan-out into row submissions
     * ----------------------------------------------------------------
     * - If FormActionsContext produced bulkRows, we must treat each row
     *   as a separate submission record for draft/testing display.
     * - We keep a parent submissionId and derive per-row submissionIds:
     *     `${submissionId}::${rowIndex}`
     * ---------------------------------------------------------------- */
    const rowSubmissions = (Array.isArray(bulkRows) && bulkRows.length > 0)
      ? bulkRows.map((r) => ({
          rowIndex: r.rowIndex,
          submissionId: `${submissionId}::${r.rowIndex}`,
          submissionData: r.submissionData || [],
          clientRowId: r.clientRowId || null,
        }))
      : [{
          rowIndex: 0,
          submissionId,
          submissionData: processedSubmission,
          clientRowId: null,
        }];

    console.log("[FormSubmissionsContext][bulk] rowSubmissions prepared:", {
      hasBulk: Array.isArray(bulkRows) && bulkRows.length > 0,
      count: rowSubmissions.length,
      sample: rowSubmissions.slice(0, 3).map(r => ({
        rowIndex: r.rowIndex,
        submissionId: r.submissionId,
        clientRowId: r.clientRowId,
      })),
    });

    /* ────────────────────────────────────────────────────────────────
     * Helper: build a single finalSubmissionObject from a row submissionData
     * - Preserves your existing flattenedSubmission + responses schema
     * - Computes displayInCardMessage per row (if enabled) using template
     *   so bulk rows get correct message too (not just row 0).
     * ---------------------------------------------------------------- */
    const buildFinalSubmissionObjectForRow = (rowIdx, rowSubmissionId, rowSubmissionData) => {
      // 1) Extract any displayInCardMessage field if present (row0 may have it)
      let rowDisplayInCardMessage = "";

      let extractedRowSubmissionData = Array.isArray(rowSubmissionData)
        ? [...rowSubmissionData]
        : [];

      if (submissionSettings?.displayInCard?.enabled) {
        extractedRowSubmissionData = extractedRowSubmissionData.filter((field) => {
          if (field.id === "displayInCardMessage") {
            rowDisplayInCardMessage = field.value || "";
            return false;
          }
          return true;
        });

        // If this row doesn't already contain displayInCardMessage (bulk rows >0),
        // compute it from the template using the row’s current values.
        if (!rowDisplayInCardMessage && submissionSettings?.displayInCard?.customMessage) {
          const chipValues = extractedRowSubmissionData.reduce((acc, field) => {
            const v = field.value ?? "";
            if (field.ufi) acc[field.ufi] = v;
            if (field.id)  acc[field.id]  = v;
            return acc;
          }, {});

          let transformed = replaceChipsWithValues(
            submissionSettings.displayInCard.customMessage,
            chipValues,
            "final"
          );

          // Strip HTML tags (same approach you used in FormActionsContext)
          const parser = new DOMParser();
          const doc = parser.parseFromString(transformed, "text/html");
          rowDisplayInCardMessage = doc.body.textContent || "";
        }
      }

      // 2) Build submissionActions per row (displayInCard differs by row)
      const submissionActions = submissionSettings
        ? {
            displayInCard: submissionSettings.displayInCard?.enabled
              ? rowDisplayInCardMessage
              : null,
            sendEmail: submissionSettings.sendEmail?.enabled
              ? submissionSettings.sendEmail
              : null,
            redirect: submissionSettings.redirect?.enabled
              ? submissionSettings.redirect
              : null,
            displayToast: submissionSettings.displayToast?.enabled
              ? submissionSettings.displayToast
              : null,
            addToSubmissionsTable: submissionSettings.addToSubmissionsTable?.enabled
              ? submissionSettings.addToSubmissionsTable
              : null,
          }
        : {};

      // 3) Build both shapes: flattenedSubmission + nestedResponses
      const { flattenedSubmission, nestedResponses } = extractedRowSubmissionData.reduce(
        (acc, field) => {
          const key        = field.ufi;
          const value      = field.value ?? "";
          const labelKey   = `label_${key}`;
          const humanLabel = field.label || "Unknown Field";

          acc.flattenedSubmission[key]      = value;
          acc.flattenedSubmission[labelKey] = humanLabel;

          acc.nestedResponses[key]          = { value };
          acc.nestedResponses[labelKey]     = humanLabel;

          return acc;
        },
        { flattenedSubmission: {}, nestedResponses: {} }
      );

      // 4) Final submission object (matches your existing schema)
      const finalSubmissionObject = {
        submissionId: rowSubmissionId,
        formId,
        createdAt   : new Date().toISOString(),
        submittedAt : new Date(),
        submitterId : viewerId,
        submitterSessionId: viewerSessionId,
        ...flattenedSubmission,
        responses   : nestedResponses,
        submissionActions: JSON.stringify(submissionActions),
      };

      // Only add displayInCardMessage if displayInCard is enabled
      if (submissionSettings?.displayInCard?.enabled) {
        finalSubmissionObject.displayInCardMessage          = rowDisplayInCardMessage;
        finalSubmissionObject[`label_displayInCardMessage`] = "Submission";
      }

      if (resolvedOrganizationId) {
        finalSubmissionObject.organizationId = resolvedOrganizationId;
      }

      // Include parent grouping id for future “group by submit click” if needed
      finalSubmissionObject.bulkParentId = submissionId;
      finalSubmissionObject.bulkRowIndex = rowIdx;

      return finalSubmissionObject;
    };

    const isDisplayCardBulkSubmission = rowSubmissions.length > 1;

    // ────────────────────────────────────────────────────────────────
    // Display-in-Card: bulk rows support (instant UI, runtime-only)
    // ----------------------------------------------------------------
    // Build per-row messages from the template and store them in
    // submissionSettings.displayInCard.submittedRows WITHOUT:
    //   • toggling enabled actions
    //   • persisting to storage
    //
    // Enterprise integration note:
    // - submittedRows is kept for backwards compatibility with the current UI.
    // - displayCardOutputRows is the row-aware runtime contract used by
    //   DisplayCardPreview to attach integration output, such as Bitly,
    //   to the correct generated URL row.
    // - This is runtime-only. It does not block persistence, call Bitly,
    //   or change Firestore submission schema.
    // - The generated output is patched before any Firestore/Bitly work, so
    //   bulk Generated URL display remains as instant as the existing behavior.
    // - Single-row submissions intentionally clear stale bulk runtime state
    //   and continue using the lean submittedValues + latestSubmissionDocId path.
    // ────────────────────────────────────────────────────────────────
    let displayCardOutputRows = [];

    if (submissionSettings?.displayInCard?.enabled && typeof patchActionRuntime === 'function') {
      try {
        if (!isDisplayCardBulkSubmission) {
          // Single-row Display Card output should keep the normal submittedValues
          // + latestSubmissionDocId flow. Clear stale bulk runtime rows so a
          // previous bulk submission cannot leak into the next single submission.
          patchActionRuntime('displayInCard', {
            submittedRows: [],
            displayCardOutputRows: [],
            latestSubmissionDocIds: [],
          });

          console.log('[FormSubmissionsContext][displayInCard] cleared bulk runtime rows for single submission.');
        } else {
          const displayCardRowObjects = rowSubmissions
            .map((r) => {
              const rowObj = buildFinalSubmissionObjectForRow(
                r.rowIndex,
                r.submissionId,
                r.submissionData
              );

              const generatedText = String(rowObj?.displayInCardMessage || '').trim();

              return {
                sourceRow: r,
                rowObj,
                generatedText,
              };
            })
            .filter((entry) => Boolean(entry.generatedText));

          const bulkMessages = displayCardRowObjects
            .map((entry) => entry.generatedText);

          displayCardOutputRows = displayCardRowObjects.map((entry, idx) => ({
            rowIndex: entry.sourceRow.rowIndex ?? idx,
            clientRowId: entry.sourceRow.clientRowId || null,

            // Client-side row submission id, e.g. parentSubmissionId::0.
            // This is stable before Firestore returns the real document id.
            submissionId: entry.sourceRow.submissionId,

            // Filled after persistence succeeds. DisplayCardPreview will use this
            // to listen to the exact formSubmissions/{docId} that Bitly enriches.
            submissionDocId: null,

            // Lets the UI distinguish instant generated output from persisted output
            // without delaying the Generated URL render.
            persistenceStatus: 'pending',

            // Plain generated output shown in Display Card.
            generatedText: entry.generatedText,

            // Runtime integration state is intentionally lightweight here.
            // The backend still owns Bitly creation and result writes.
            integrationRuntime:
              isPublishedMode &&
              submissionSettings?.displayInCard?.enableBitly === true
                ? {
                    bitly: {
                      provider: 'bitly',
                      status: 'queued',
                    },
                  }
                : {},
          }));

          patchActionRuntime('displayInCard', {
            submittedRows: bulkMessages,
            displayCardOutputRows,
            latestSubmissionDocId: null,
            latestSubmissionDocIds: [],
          });

          console.log('[FormSubmissionsContext][displayInCard][bulk] patched submittedRows (runtime-only):', {
            count: bulkMessages.length,
            sample: bulkMessages.slice(0, 3),
          });

          console.log('[FormSubmissionsContext][displayInCard][bulk] patched displayCardOutputRows (runtime-only):', {
            count: displayCardOutputRows.length,
            sample: displayCardOutputRows.slice(0, 3).map((row) => ({
              rowIndex: row.rowIndex,
              clientRowId: row.clientRowId,
              submissionId: row.submissionId,
              submissionDocId: row.submissionDocId,
              persistenceStatus: row.persistenceStatus,
              hasGeneratedText: Boolean(row.generatedText),
              bitlyStatus: row.integrationRuntime?.bitly?.status || null,
            })),
          });
        }
      } catch (e) {
        console.warn('[FormSubmissionsContext][displayInCard][bulk] failed building submittedRows:', e?.message || e);
      }
    }

    /**
     * Builds the persisted version of Display Card output rows.
     *
     * Enterprise integration note:
     * - This helper runs only after persistence returns doc ids.
     * - It does not create submissions.
     * - It does not call Bitly.
     * - It simply attaches the real Firestore submission doc id to the
     *   already-rendered generated output row so the UI can hydrate per-row
     *   backend enrichment later.
     * - The same row shape can be reused by future output actions because it
     *   keeps row identity, client identity, persistence identity, and integration
     *   runtime separate from the displayed generated value.
     */
    const buildPersistedDisplayCardOutputRows = ({
      singleSubmissionDocId = null,
      resultByLocalId = null,
      failedLocalIds = [],
    } = {}) => {
      const failedLocalIdSet = new Set(failedLocalIds || []);

      return displayCardOutputRows
        .filter((row) => {
          if (!row) return false;

          if (!resultByLocalId) {
            return true;
          }

          const localId = `local_${row.submissionId}`;
          return !failedLocalIdSet.has(localId);
        })
        .map((row) => {
          const localId = `local_${row.submissionId}`;
          const match =
            resultByLocalId && typeof resultByLocalId.get === 'function'
              ? resultByLocalId.get(localId)
              : null;

          const submissionDocId =
            singleSubmissionDocId ||
            match?.realId ||
            row.submissionDocId ||
            null;

          return {
            ...row,
            submissionDocId,
            persistenceStatus: submissionDocId ? 'persisted' : row.persistenceStatus,
            integrationRuntime:
              submissionSettings?.displayInCard?.enableBitly === true
                ? {
                    ...row.integrationRuntime,
                    bitly: {
                      provider: 'bitly',
                      status: 'queued',
                    },
                  }
                : row.integrationRuntime,
          };
        });
    };
    // 3️⃣ Extract displayInCardMessage ONLY IF displayInCard is enabled
    let displayInCardMessage    = "";
    let extractedSubmissionData = [...processedSubmission];


    if (submissionSettings?.displayInCard?.enabled) {
      extractedSubmissionData = processedSubmission.filter((field) => {
        if (field.id === "displayInCardMessage") {
          displayInCardMessage = field.value || "";
          return false;
        }
        return true;
      });

//      console.log("[FormSubmissionsContext] Extracted displayInCardMessage:", displayInCardMessage);
    }

    // 4️⃣ Ensure submission settings align with processed data
    const submissionActions = submissionSettings
      ? {
          displayInCard:        submissionSettings.displayInCard?.enabled
            ? displayInCardMessage
            : null,
          sendEmail:            submissionSettings.sendEmail?.enabled
            ? submissionSettings.sendEmail
            : null,
          redirect:             submissionSettings.redirect?.enabled
            ? submissionSettings.redirect
            : null,
          displayToast:         submissionSettings.displayToast?.enabled
            ? submissionSettings.displayToast
            : null,
          addToSubmissionsTable: submissionSettings.addToSubmissionsTable?.enabled
            ? submissionSettings.addToSubmissionsTable
            : null,
        }
      : {};

    console.log(
      "[FormSubmissionsContext] Submission Actions Before Saving:",
      submissionActions
    );

    const actionOutputFields = {
      submissionActions: JSON.stringify(submissionActions),
    };

    if (submissionSettings?.displayInCard?.enabled) {
      actionOutputFields.displayInCardMessage = displayInCardMessage;
      actionOutputFields.label_displayInCardMessage = "Submission";
    }

    /* ------------------------------------------------------------------
     * 5️⃣  Build BOTH shapes:
     *      • flattenedSubmission  (legacy – used by BigQuery, DisplayInCard)
     *      • nestedResponses      (new – what DraftSubmissionTable expects)
     * ------------------------------------------------------------------ */
    const { flattenedSubmission, nestedResponses } = extractedSubmissionData.reduce(
      (acc, field) => {
        const key           = field.ufi;                       // unique key (UFI)
        const value         = field.value ?? "";
        const labelKey      = `label_${key}`;
        const humanLabel    = field.label || "Unknown Field";

        /* ---- flattened (existing behaviour) ---- */
        acc.flattenedSubmission[key]      = value;
        acc.flattenedSubmission[labelKey] = humanLabel;

        /* ---- nested (new for table) ------------ */
        acc.nestedResponses[key]          = { value };
        acc.nestedResponses[labelKey]     = humanLabel;

        return acc;
      },
      { flattenedSubmission: {}, nestedResponses: {} }
    );

    console.log(
      "[FormSubmissionsContext] Flattened submission data with correct field mappings:",
      flattenedSubmission
    );
    console.log(
      "[FormSubmissionsContext] Nested responses map for table preview:",
      nestedResponses
    );

    // 6️⃣  Create final submission object
    const finalSubmissionObject = {
      submissionId,
      formId,
      createdAt: new Date().toISOString(),
      submittedAt: new Date(),
      submitterId: viewerId,
      submitterSessionId: viewerSessionId,
      ...flattenedSubmission,
      ...actionOutputFields,
      responses: nestedResponses,
    };


/* ------------------------------------------------------------------
 * Extract asset references from the processed submission
 * We look for attach/image fields whose final value is an object
 * containing { assetId } (or legacy { id } from the library).
 * No hard-coded UFIs — fully dynamic and per form.
 * ------------------------------------------------------------------ */
const assetsFromSubmission = (extractedSubmissionData || [])
  .filter((f) => f && (f.type === "attach" || f.type === "image"))
  .flatMap((f) => {
    const v = f?.value;
    if (!v) return [];
    // Single object
    if (typeof v === "object" && !Array.isArray(v)) {
      const aid = v.assetId || v.id || null;
      return aid ? [{ ufi: f.ufi, assetId: aid }] : [];
    }
    // Array of objects
    if (Array.isArray(v)) {
      return v
        .map((it) => (it && typeof it === "object" ? (it.assetId || it.id) : null))
        .filter(Boolean)
        .map((aid) => ({ ufi: f.ufi, assetId: aid }));
    }
    return [];
  });

/* ────────────────────────────────────────────────────────────────
 * NEW: Extract asset references from a *row* submissionData
 * ----------------------------------------------------------------
 * Bulk published writes need per-row assets (not just row0).
 * Mirrors the single-row assetsFromSubmission logic above.
 * ---------------------------------------------------------------- */
const extractAssetsFromRowSubmissionData = (rowSubmissionData = []) => {
  return (rowSubmissionData || [])
    .filter((f) => f && (f.type === "attach" || f.type === "image"))
    .flatMap((f) => {
      const v = f?.value;
      if (!v) return [];
      // Single object
      if (typeof v === "object" && !Array.isArray(v)) {
        const aid = v.assetId || v.id || null;
        return aid ? [{ ufi: f.ufi, assetId: aid }] : [];
      }
      // Array of objects
      if (Array.isArray(v)) {
        return v
          .map((it) => (it && typeof it === "object" ? (it.assetId || it.id) : null))
          .filter(Boolean)
          .map((aid) => ({ ufi: f.ufi, assetId: aid }));
      }
      return [];
    });
};

// Generated action output fields are already merged via actionOutputFields.


//    console.log("[FormSubmissionsContext] Final Submission Object:", finalSubmissionObject);

// Append organizationId from resolved context/form source
//    console.log("[FormSubmissionsContext] organizationId from resolved context/form source:", resolvedOrganizationId);
if (resolvedOrganizationId) {
  finalSubmissionObject.organizationId = resolvedOrganizationId;
} else {
  console.warn(
    "[FormSubmissionsContext] No organizationId found from AuthContext or form document. BigQuery insert and integration enrichment may be skipped by the Cloud Function."
  );
}

// 7️⃣ Save data depending on form mode
if (
  isPublishedMode &&
  submissionSettings?.displayInCard?.enabled &&
  submissionSettings?.displayInCard?.enableBitly === true
) {
  patchDisplayCardSubmissionRuntime({
    latestSubmissionDocId: null,
    latestSubmissionDocIds: [],
    integrationRuntime: {
      bitly: {
        provider: 'bitly',
        status: 'queued',
      },
    },
  });
}

if (isTestingMode || isDraftMode) {
  const existingDraftSubmissions =
    safeGetItem(`draft_${formId}_submission`) || [];

  // Build N final submission objects
  const newRows = rowSubmissions.map((r) =>
    buildFinalSubmissionObjectForRow(r.rowIndex, r.submissionId, r.submissionData)
  );

  // Append rows if not already present
  newRows.forEach((rowObj) => {
    if (!existingDraftSubmissions.some((sub) => sub.submissionId === rowObj.submissionId)) {
      existingDraftSubmissions.push(rowObj);
    }
  });

  safeSetItem(`draft_${formId}_submission`, existingDraftSubmissions);
  setSubmissions(existingDraftSubmissions);
  setSubmitStatus("Draft saved successfully.");

  // keep rowCount feeling correct in preview
  setRowCount(existingDraftSubmissions.length);

  await recordSubmissionOnboardingEvent({
    submissionId,
    eventSubmissionId: submissionId,
    submissionMode: SUBMISSION_MODES.DRAFT_OR_TEST,
    isBulk: rowSubmissions.length > 1,
    rowCount: rowSubmissions.length,
    persistedCount: rowSubmissions.length,
    failedCount: 0,
    metadata: {
      formMode: isTestingMode ? 'testing' : 'draft',
      branch: 'draft_or_testing',
    },
  });

} else {
  /* ──────────────────────────────────────────────────────────────
     PUBLISHED FORMS: submit to Cloud Function (server enrichment)
     ──────────────────────────────────────────────────────────── */

  // Hoist so catch { } can see it
  let placeholderId = null;

  /* ────────────────────────────────────────────────────────────────
   * NEW: BULK published fan-out (URL CSV / future multi-select)
   * ----------------------------------------------------------------
   * If rowSubmissions contains >1 row, write N rows to Firestore/CF.
   * - Instant UI: insert N optimistic rows immediately
   * - Persistence: per-row addDoc (fast path) OR per-row CF call (assets)
   * - Safety: Promise.allSettled + rollback only failed placeholders
   * ---------------------------------------------------------------- */
  const isBulk = Array.isArray(rowSubmissions) && rowSubmissions.length > 1;

  if (isBulk) {
    try {
      // ✅ UPDATED: enforce auth only when the form is NOT public
      if (!currentUser && !canSubmitAnonymously) {
        throw new Error("No authenticated user for published submission.");
      }

      // Build optimistic placeholder rows FIRST (instant UI)
      const optimisticRows = rowSubmissions.map((r) => {
        // Reuse your canonical row object builder so schema stays identical
        const rowObj = buildFinalSubmissionObjectForRow(
          r.rowIndex,
          r.submissionId,
          r.submissionData
        );

        return {
          id: `local_${r.submissionId}`, // stable per row
          formId,
          submitterId: viewerId,
          submitterSessionId: viewerSessionId,

          // Preserve your schema: responses + flattened fields
          ...rowObj,
          organizationId: orgIdForDocs, // force final (prevents overwrite)
          responses: rowObj.responses,  // force final (prevents overwrite)

          submittedAt: new Date(),       // local for UX; server will overwrite
          _linkedAssetsJson: "[]",
          _pendingEnrichment: true,
          ...buildDisplayTableBitlyPendingPatch(),

          // bulk group fields (already in rowObj but keep explicit)
          bulkParentId: submissionId,
          bulkRowIndex: r.rowIndex,
        };
      });


      console.log("[FormSubmissionsContext][bulk][published] inserting optimistic rows:", {
        count: optimisticRows.length,
        first3: optimisticRows.slice(0, 3).map(r => r.id),
      });

      // Prepend optimistics to UI (so newest appears first)
      setSubmissions((prev) => [...optimisticRows, ...prev]);

      // Counter feels instant too
      setRowCount((c) => (c > -1 ? c + optimisticRows.length : c));

      // Decide per-row mode
      const submissionMode =
        userData && typeof userData === "object"
          ? userData.submissionMode || null
          : null;

      // Token once (only used when we call CF)
      const idToken = currentUser?.getIdToken ? await currentUser.getIdToken() : null;

      // Small concurrency cap (enterprise safety)
      const CONCURRENCY = 5;

      const runPool = async (items, worker, concurrency = 5) => {
        const results = [];
        let idx = 0;
        const runners = new Array(Math.min(concurrency, items.length)).fill(0).map(async () => {
          while (idx < items.length) {
            const myIdx = idx++;
            try {
              results[myIdx] = await worker(items[myIdx], myIdx);
              } catch (e) {
                results[myIdx] = {
                  __error: e,
                  localId: `local_${items[myIdx]?.submissionId}`, // critical
                };
              }

          }
        });
        await Promise.all(runners);
        return results;
      };

      // Worker: persist one row
      const persistOneRow = async (r) => {
        const rowObj = buildFinalSubmissionObjectForRow(
          r.rowIndex,
          r.submissionId,
          r.submissionData
        );

        // Derive per-row assets
        const rowAssets = extractAssetsFromRowSubmissionData(r.submissionData);

        // Public anonymous → direct write only (no token)
        if (!currentUser && canSubmitAnonymously) {
          const docRef = await addDoc(collection(db, "formSubmissions"), {
            formId,
            submitterId: viewerId,
            submitterSessionId: viewerSessionId,

            ...rowObj,
            organizationId: orgIdForDocs, //  force final
            responses: rowObj.responses,  //  force final

            _linkedAssetsJson: "[]",
            _publicSubmission: true,
            submittedAt: serverTimestamp(),

            bulkParentId: submissionId,
            bulkRowIndex: r.rowIndex,
          });

          return { ok: true, localId: `local_${r.submissionId}`, realId: docRef.id };
        }


        // Auth required from here
        if (!currentUser) throw new Error("No authenticated user for bulk published row.");

        // FAST PATH: no assets and not assetEdit → direct addDoc
        if (submissionMode !== "assetEdit" && (!rowAssets || rowAssets.length === 0)) {
          const docRef = await addDoc(collection(db, "formSubmissions"), {
            formId,
            submitterId: viewerId,
            submitterSessionId: viewerSessionId,

            ...rowObj,
            organizationId: orgIdForDocs, // force final
            responses: rowObj.responses,  // force final

            _linkedAssetsJson: "[]",
            submittedAt: serverTimestamp(),

            bulkParentId: submissionId,
            bulkRowIndex: r.rowIndex,
          });

          return { ok: true, localId: `local_${r.submissionId}`, realId: docRef.id };
        }


        // ASSET PATH: call CF per row
        if (!idToken) throw new Error("Missing ID token for submitFormWithAssets bulk row.");

        // Build flattenedSubmission + nestedResponses from rowObj.responses
        // We keep your existing schema: both shapes are sent.
        const flattenedSubmission = {};
        const nestedResponses = rowObj.responses || {};

        Object.keys(nestedResponses || {}).forEach((k) => {
          const v = nestedResponses[k];
          // Keep label_* strings as-is
          if (k.startsWith("label_")) {
            flattenedSubmission[k] = v;
          } else {
            // { value } objects
            flattenedSubmission[k] =
              (v && typeof v === "object" && "value" in v) ? v.value : v;
          }
        });

        const payload = {
          formId,
          organizationId: orgIdForPayload,
          submitterId: viewerId,
          submitterSessionId: viewerSessionId,

          flattenedSubmission,
          nestedResponses,

          assets: rowAssets, // [{ ufi, assetId }]
          mode: submissionMode,
          editingAssetId: userData?.editingAssetId || null,
          targetVersion: userData?.targetVersion ?? null,
        };

        const out = await submitFormWithAssetsHttp(idToken, payload);
        const linkedJson = JSON.stringify(out?.linkedAssets || []);

        return {
          ok: true,
          localId: `local_${r.submissionId}`,
          realId: out?.submissionId || null,
          linkedJson,
        };
      };

      // Persist all rows with a concurrency cap
      const results = await runPool(rowSubmissions, persistOneRow, CONCURRENCY);

      // Apply results to UI (replace optimistic ids + enrichment flags)
      // PERF: results.find(...) inside prev.map(...) is O(prevRows * bulkRows).
      // Build a lookup once so each row reconciliation is O(1).
      const resultByLocalId = new Map(
        (results || [])
          .filter((res) => res && res.localId)
          .map((res) => [res.localId, res])
      );

      setSubmissions((prev) =>
        prev.map((row) => {
          if (!row || typeof row !== "object") return row;

          const match = resultByLocalId.get(row.id);
          if (!match) return row;

          if (match.__error) return row; // keep pending; we’ll roll back below

          return {
            ...row,
            id: match.realId || row.id,
            _linkedAssetsJson: match.linkedJson || row._linkedAssetsJson || "[]",
            _pendingEnrichment: false,
            // Keep _pendingShortlinks intact after id reconciliation.
            // The table-local hydration hook will replace this UI-only hint when
            // Firestore writes shortlinks.bitly.shortUrl/status for this row.
          };
        })
      );

      // Roll back failures (remove rows whose persistence failed)
      const failedLocalIds = results
        .filter((r) => r && r.__error && r.localId)
        .map((r) => r.localId)
        .filter(Boolean);

      const persistedCount = results.filter((r) => {
        return r && r.ok === true && !r.__error;
      }).length;

      if (failedLocalIds.length > 0) {
        console.warn("[FormSubmissionsContext][bulk][published] Some rows failed; rolling back:", {
          failedCount: failedLocalIds.length,
          failedLocalIds: failedLocalIds.slice(0, 10),
        });

        setSubmissions((prev) => prev.filter((r) => !failedLocalIds.includes(r.id)));
        setRowCount((c) => (c > -1 ? c - failedLocalIds.length : c));
        setError("Some bulk rows failed to submit. Please retry.");
      } else {
        setSubmitStatus("Form submitted successfully.");
      }

      const persistedDocIds = results
        .filter((result) => result?.ok === true && result.realId)
        .map((result) => result.realId);

      const persistedDisplayCardOutputRows = buildPersistedDisplayCardOutputRows({
        resultByLocalId,
        failedLocalIds,
      });

      const shouldPatchDisplayCardBulkRuntime =
        submissionSettings?.displayInCard?.enabled === true &&
        (
          persistedDocIds.length > 0 ||
          persistedDisplayCardOutputRows.length > 0
        );

      if (shouldPatchDisplayCardBulkRuntime) {
        patchDisplayCardSubmissionRuntime({
          latestSubmissionDocId:
            persistedDocIds.length === 1 ? persistedDocIds[0] : null,
          latestSubmissionDocIds: persistedDocIds,
          displayCardOutputRows: persistedDisplayCardOutputRows,
          integrationRuntime: {
            bitly: {
              provider: 'bitly',
              status: 'queued',
            },
          },
        });

        console.log('[FormSubmissionsContext][displayInCard][bulk] patched persisted displayCardOutputRows:', {
          persistedDocIds,
          outputRowCount: persistedDisplayCardOutputRows.length,
          sample: persistedDisplayCardOutputRows.slice(0, 3).map((row) => ({
            rowIndex: row.rowIndex,
            clientRowId: row.clientRowId,
            submissionId: row.submissionId,
            submissionDocId: row.submissionDocId,
            persistenceStatus: row.persistenceStatus,
            bitlyStatus: row.integrationRuntime?.bitly?.status || null,
          })),
        });
      }

      if (persistedCount > 0) {
        await recordSubmissionOnboardingEvent({
          submissionId,
          eventSubmissionId: submissionId,
          submissionMode: SUBMISSION_MODES.PUBLISHED,
          isBulk: true,
          rowCount: rowSubmissions.length,
          persistedCount,
          failedCount: failedLocalIds.length,
          metadata: {
            formMode: 'published',
            branch: 'bulk_published',
          },
        });
      }

      return; // IMPORTANT: stop here so single-row path does not run
    } catch (bulkErr) {
      console.error("[FormSubmissionsContext][bulk][published] bulk submit failed:", bulkErr);
      // If the whole bulk flow fails before we can reconcile,
      // leave UI as-is but report error.
      setError(bulkErr?.message || "Failed to submit bulk rows.");
      setSubmitStatus("");
      return;
    }
  }

  // ────────────────────────────────────────────────────────────────
  // Existing single-row published logic continues below unchanged
  // ────────────────────────────────────────────────────────────────
  try {
    // UPDATED: enforce auth only when the form is NOT public
    // - Public form → allow anonymous submit
    // - Non-public form → require auth (RequireAccess already enforced view rights)
    if (!currentUser && !canSubmitAnonymously) {
      throw new Error("No authenticated user for published submission.");
    }


    /* ── Optimistic row BEFORE network ───────────────────────── */
    placeholderId = `local_${Date.now()}`;
    setSubmissions((prev) => [
      {
        id: placeholderId,
        formId,
        organizationId: orgIdForDocs,
        submitterId: viewerId,
        submitterSessionId: viewerSessionId,
        responses: nestedResponses,
        ...flattenedSubmission,
        ...actionOutputFields,
        submittedAt: new Date(),    // (nice for local sort)
        _linkedAssetsJson: "[]",    // (optional clarity)
        _pendingEnrichment: true,
        ...buildDisplayTableBitlyPendingPatch(),
      },
      ...prev,
    ]);

    // keep the counter feeling snappy too
    setRowCount((c) => (c > -1 ? c + 1 : c));

    /* ──────────────────────────────────────────────────────────
     * PUBLIC/ANON SUBMIT PATH
     * Public viewers do not have an ID token, so they cannot call
     * submitFormWithAssetsHttp (Authorization Bearer token).
     *
     * Policy: for public forms, write directly to Firestore.
     * - still records submitterSessionId for "only mine" tables
     * - skips server enrichment / BigQuery / asset-linking CF
     * - preserves your existing submission schema
     * ────────────────────────────────────────────────────────── */
    if (!currentUser && canSubmitAnonymously) {
      try {
        const docRef = await addDoc(collection(db, 'formSubmissions'), {
          formId,
          organizationId: orgIdForDocs,
          submitterId: viewerId,
          submitterSessionId: viewerSessionId,
          responses: nestedResponses,
          ...flattenedSubmission,
          ...actionOutputFields,
          _linkedAssetsJson: "[]",
          _publicSubmission: true,               // optional marker for debugging
          submittedAt: serverTimestamp(),
        });

        // Replace the placeholder with the real doc id (no extra network)
        setSubmissions((prev) =>
          prev.map((r) =>
            r.id === placeholderId
              ? {
                  ...r,
                  id: docRef.id,
                  _pendingEnrichment: false,
                  // Preserve _pendingShortlinks for table-local hydration.
                  // This row now has a real Firestore doc id, so the Display Table
                  // hydration hook can listen to exactly this submission record.
                }
              : r
          )
        );

        patchDisplayCardSubmissionRuntime({
          latestSubmissionDocId: docRef.id,
          latestSubmissionDocIds: [],
          displayCardOutputRows: [],
          integrationRuntime: {
            bitly: {
              provider: 'bitly',
              status: 'queued',
            },
          },
        });

        setSubmitStatus("Form submitted successfully.");
        return; //  done; skip the function call
      } catch (clientWriteErr) {
        // Roll back placeholder on failure
        setSubmissions((prev) => prev.filter((r) => r.id !== placeholderId));
        setRowCount((c) => (c > -1 ? c - 1 : c));
        throw clientWriteErr;
      }
    }

    /* ──────────────────────────────────────────────────────────
     * FAST PATH (plain forms only):
     * Skip CF *only* when NOT in 'assetEdit' mode.
     * ────────────────────────────────────────────────────────── */
    const submissionMode =
      userData && typeof userData === 'object'
        ? userData.submissionMode || null
        : null;

    if (
      submissionMode !== 'assetEdit' &&
      (!assetsFromSubmission || assetsFromSubmission.length === 0)
    ) {
      try {
        const docRef = await addDoc(collection(db, 'formSubmissions'), {
          formId,
          organizationId: orgIdForDocs,
          submitterId: viewerId,
          submitterSessionId: viewerSessionId,
          responses: nestedResponses,
          ...flattenedSubmission,
          ...actionOutputFields,
          _linkedAssetsJson: "[]",
          submittedAt: serverTimestamp(),
        });

        // Replace the placeholder with the real doc id (no extra network)
        setSubmissions((prev) =>
          prev.map((r) =>
            r.id === placeholderId
              ? {
                  ...r,
                  id: docRef.id,
                  _pendingEnrichment: false,
                  // Preserve _pendingShortlinks for table-local hydration.
                  // This row now has a real Firestore doc id, so the Display Table
                  // hydration hook can listen to exactly this submission record.
                }
              : r
          )
        );

        patchDisplayCardSubmissionRuntime({
          latestSubmissionDocId: docRef.id,
          latestSubmissionDocIds: [],
          displayCardOutputRows: [],
          integrationRuntime: {
            bitly: {
              provider: 'bitly',
              status: 'queued',
            },
          },
        });

        setSubmitStatus("Form submitted successfully.");

        await recordSubmissionOnboardingEvent({
          submissionId,
          eventSubmissionId: docRef.id,
          submissionMode: SUBMISSION_MODES.PUBLISHED,
          isBulk: false,
          rowCount: 1,
          persistedCount: 1,
          failedCount: 0,
          metadata: {
            formMode: 'published',
            branch: 'published_fast_path',
          },
        });

        return; // ← done; skip the function call
      } catch (clientWriteErr) {
        // Roll back placeholder on failure
        setSubmissions((prev) => prev.filter((r) => r.id !== placeholderId));
        setRowCount((c) => (c > -1 ? c - 1 : c));
        throw clientWriteErr;
      }
    }

    /* ────────────────────────────────────────────────────────── */

    const idToken = await currentUser.getIdToken();

    console.log(
      '[SUBMIT:TIMER] before submitFormWithAssetsHttp',
      Math.round(((typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now()) - __t0),
      'ms'
    );

    const payload = {
      formId,
      organizationId: orgIdForPayload,
      submitterId: viewerId,
      submitterSessionId: viewerSessionId,
      flattenedSubmission: {
        ...flattenedSubmission,
        ...actionOutputFields,
      },
      nestedResponses,
      assets: assetsFromSubmission,   // [{ ufi, assetId }]
      mode: userData?.submissionMode || null,
      editingAssetId: userData?.editingAssetId || null,
      targetVersion: userData?.targetVersion ?? null,
    };

    const out = await submitFormWithAssetsHttp(idToken, payload);

    console.log(
      '[SUBMIT:TIMER] after submitFormWithAssetsHttp',
      Math.round(((typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now()) - __t0),
      'ms',
      { ok: out?.ok, submissionId: out?.submissionId }
    );

    console.log("[FormSubmissionsContext] submitFormWithAssets OK:", out);

    // The function already enriched the row and returns the linked assets.
    // Stash the snapshot so AUF virtual columns render immediately.
    const linkedJson = JSON.stringify(out?.linkedAssets || []);

    /* Replace the optimistic placeholder with the enriched row */
    setSubmissions((prev) =>
      prev.map((r) =>
        r.id === placeholderId
          ? {
              id: out?.submissionId || placeholderId,
              formId,
              organizationId: orgIdForDocs,
              submitterId: viewerId,
              submitterSessionId: viewerSessionId,
              responses: nestedResponses,
              ...flattenedSubmission,
              ...actionOutputFields,
              _linkedAssetsJson: linkedJson,
              submittedAt: new Date(),       // local timestamp for UX sort
              _pendingEnrichment: false,     // no longer pending
              ...(
                r?._pendingShortlinks
                  ? { _pendingShortlinks: r._pendingShortlinks }
                  : {}
              ),
            }
          : r
      )
    );

    if (out?.submissionId) {
      patchDisplayCardSubmissionRuntime({
        latestSubmissionDocId: out.submissionId,
        latestSubmissionDocIds: [],
        displayCardOutputRows: [],
        integrationRuntime: {
          bitly: {
            provider: 'bitly',
            status: 'queued',
          },
        },
      });
    }

    // Do NOT bump rowCount again; we already bumped when we added the placeholder.
    setSubmitStatus("Form submitted successfully.");

    await recordSubmissionOnboardingEvent({
      submissionId,
      eventSubmissionId: out?.submissionId || submissionId,
      submissionMode: SUBMISSION_MODES.PUBLISHED,
      isBulk: false,
      rowCount: 1,
      persistedCount: 1,
      failedCount: 0,
      metadata: {
        formMode: 'published',
        branch: 'published_asset_path',
        assetCount: assetsFromSubmission?.length || 0,
      },
    });

    // Optional: to show server timestamps immediately, you could re-load:
    // await fetchSubmissions(false);

  } catch (fnErr) {
    console.error(
      "[FormSubmissionsContext] submitFormWithAssets failed:",
      fnErr
    );

    /* Roll back the optimistic placeholder row and the count */
    setSubmissions((prev) => prev.filter((r) => r.id !== placeholderId));
    setRowCount((c) => (c > -1 ? c - 1 : c));

    setError(fnErr.message || "Failed to submit form.");
  }

}

  } catch (err) {
    setError(err.message);
    console.error("[FormSubmissionsContext] Submission error:", err);
  } finally {
    console.log(
      '[SUBMIT:TIMER] handleSubmit total',
      Math.round(((typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now()) - __t0),
      'ms'
    );
    setLoading(false);
  }
};



  /**
   * Clears draft submissions from session storage.
   */
  const clearDraftSubmissions = () => {
//    console.log(`[FormSubmissionsContext] Clearing draft submissions for form: ${formId}`);
    safeRemoveItem(`draft_${formId}_submission`);
    setSubmissions([]);
  };

  /**
   * Loads more submissions for published forms using Firestore pagination.
   */
  const loadMoreSubmissions = () => {
    if (hasMoreSubmissions) {
      fetchSubmissions(true, null, onlyMineScope);
    }
  };

  /**
   * Updates the number of submissions per page based on user selection.
   */
  const updatePageSize = (newSize) => {
    if ([10, 25, 50].includes(newSize)) {
//      console.log(`[FormSubmissionsContext] Updating page size to: ${newSize}`);
      setPageSize(newSize);
      setLastVisible(null);
      fetchSubmissions(false, newSize, onlyMineScope);
    } else {
      console.warn(`[FormSubmissionsContext] Invalid page size: ${newSize}`);
    }
  };

  return (
    <FormSubmissionsContext.Provider
      value={{
        submissions,
        userSubmissions,
        columnOrder,
        fetchSubmissions,
        totalFetched: submissions.length,
        loadMoreSubmissions,
        clearDraftSubmissions,
        handleSubmit,
        submitStatus,
        loading,
        error,
        hasMoreSubmissions,
        pageSize,
        updatePageSize,
        submissionMessage, //  Ensure `displayInCard` message is available
        getSubmissionUIComponent, 
        isTestingMode,
        rowCount,
        setDateRange,
        dateRange,
        setScope,              //expose so pages can set default scope
        patchSubmissionsById,
      }}
    >
      {children}
    </FormSubmissionsContext.Provider>
  );
};

export default FormSubmissionsProvider;
